"use client";

import { type ReactNode, useEffect, useRef } from "react";
import * as THREE from "three";
import { System } from "./system";

export type ParticlePalette = "site" | "hero";

interface ParticleSystemProps {
  children?: ReactNode;
  className?: string;
  id?: string;
  /**
   * Size the WebGL canvas to this component’s box (ResizeObserver) and use a transparent clear.
   * Use behind the landing hero so simplex noise fills only that region.
   */
  boundToContainer?: boolean;
  /** Defaults to hero when boundToContainer, else site. */
  particlePalette?: ParticlePalette;
  /** Transparent WebGL clear — use for full-viewport layers over an existing page background. */
  transparentBackground?: boolean;
}

const canCreateWebGLContext = (): boolean => {
  const canvas = document.createElement("canvas");
  const context =
    canvas.getContext("webgl2") ??
    canvas.getContext("webgl") ??
    canvas.getContext("experimental-webgl");

  return context !== null;
};

export default function ParticleSystem({
  children,
  className = "",
  boundToContainer = false,
  particlePalette: particlePaletteProp,
  transparentBackground = false,
}: ParticleSystemProps): JSX.Element {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    if (!canCreateWebGLContext()) return;

    const getSize = (): { width: number; height: number } => {
      if (boundToContainer) {
        const w = Math.max(1, Math.floor(mount.clientWidth));
        const h = Math.max(1, Math.floor(mount.clientHeight));
        return { width: w, height: h };
      }
      return { width: window.innerWidth, height: window.innerHeight };
    };

    const { width, height } = getSize();

    const particlePalette: ParticlePalette =
      particlePaletteProp ?? (boundToContainer ? "hero" : "site");
    const useAlpha = boundToContainer || transparentBackground;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 50;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: useAlpha,
        powerPreference: "high-performance",
      });
    } catch (error) {
      console.warn("ParticleSystem: WebGL renderer unavailable; skipping particle background.", error);
      return;
    }
    renderer.setSize(width, height);
    if (useAlpha) {
      renderer.setClearColor(0x000000, 0);
    }
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.top = "0";
    renderer.domElement.style.left = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";

    mount.appendChild(renderer.domElement);

    const loader: {
      scene: THREE.Scene;
      deltaTimeNormal: number;
      elapsedMilliseconds: number;
      particlePalette: ParticlePalette;
      calc: {
        rand: (a: number, b: number) => number;
        map: (x: number, inMin: number, inMax: number, outMin: number, outMax: number) => number;
      };
      ease: {
        inOutExpo: (t: number, b: number, c: number, d: number) => number;
        inExpo: (t: number, b: number, c: number, d: number) => number;
      };
      completed: boolean;
      complete: () => void;
    } = {
      scene,
      deltaTimeNormal: 0.016,
      elapsedMilliseconds: 0,
      particlePalette,
      calc: {
        rand: (a: number, b: number) => a + Math.random() * (b - a),
        map: (x: number, inMin: number, inMax: number, outMin: number, outMax: number) =>
          ((x - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin,
      },
      ease: {
        inOutExpo: (t: number, b: number, c: number, d: number) => {
          t /= d / 2;
          if (t < 1) return (c / 2) * Math.pow(2, 10 * (t - 1)) + b;
          t--;
          return (c / 2) * (-Math.pow(2, -10 * t) + 2) + b;
        },
        inExpo: (t: number, b: number, c: number, d: number) =>
          t === 0 ? b : c * Math.pow(2, 10 * (t / d - 1)) + b,
      },
      completed: false,
      complete: () => undefined,
    };

    const system = new System(loader);

    rendererRef.current = renderer;
    cameraRef.current = camera;

    const updateSize = (): void => {
      const { width: w, height: h } = getSize();
      if (cameraRef.current) {
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
      }
      if (rendererRef.current) {
        rendererRef.current.setSize(w, h);
      }
    };

    if (boundToContainer) {
      resizeObserverRef.current = new ResizeObserver(() => updateSize());
      resizeObserverRef.current.observe(mount);
    }
    window.addEventListener("resize", updateSize);

    let last = performance.now();
    const animate = (): void => {
      const now = performance.now();
      loader.deltaTimeNormal = (now - last) / 16.6667;
      loader.elapsedMilliseconds = now;
      last = now;
      system.update();
      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animate);
    };
    animate();

    return (): void => {
      window.removeEventListener("resize", updateSize);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      if (animationIdRef.current !== null) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }

      if (rendererRef.current) {
        if (rendererRef.current.domElement.parentNode === mount) {
          mount.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current.dispose();
        rendererRef.current = null;
      }

      cameraRef.current = null;
    };
  }, [boundToContainer, particlePaletteProp, transparentBackground]);

  return (
    <div ref={mountRef} className={className}>
      {children}
    </div>
  );
}
